import { useLayoutEffect, useRef, type ReactNode } from "react";
import type { LinkComponentType } from "../../../types/link";

type ChipCommon = {
  children: ReactNode;
  /** 字の前に置く印 (手描きの印、言語のロゴなど)。大きさは渡す側が決める (24 前後)。無ければ字だけ */
  icon?: ReactNode;
  /** 字の後ろの件数 */
  count?: ReactNode;
  /** 選ばれている。ボタンは aria-pressed、リンクは aria-current="page" で伝える */
  selected?: boolean;
  className?: string;
};

export type ChipProps = ChipCommon &
  (
    | {
        /** 行き先。渡すと本物の a になり、JS が無くても押せて、検索にも拾われる */
        href: string;
        /** アプリの中の移動に使うリンク (react-router の Link など)。無ければ素の a */
        LinkComponent?: LinkComponentType;
        onClick?: never;
        disabled?: never;
      }
    | { href?: undefined; LinkComponent?: never; onClick: () => void; disabled?: boolean }
  );

function ChipInner({ icon, count, children }: Pick<ChipCommon, "icon" | "count" | "children">) {
  return (
    <>
      {icon ? (
        <span className="lg-chip__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="lg-chip__label">{children}</span>
      {count !== undefined && count !== null ? <span className="lg-chip__count">{count}</span> : null}
    </>
  );
}

/**
 * 絞り込みや行き先の札。印つきの丸角の札で、選ばれた札は墨の枠 (内側にもう 1 本) と薄い地。
 * その場で絞るものはボタン (onClick)、頁を移るものは本物のリンク (href)。押せる高さは札そのものが 44px
 */
export function Chip(props: ChipProps) {
  const { icon, count, selected = false, className, children } = props;
  const cls = `lg-chip${className ? ` ${className}` : ""}`;
  if (props.href !== undefined) {
    const current = selected ? ("page" as const) : undefined;
    if (props.LinkComponent) {
      const Link = props.LinkComponent;
      return (
        <Link to={props.href} className={cls} aria-current={current}>
          <ChipInner icon={icon} count={count}>
            {children}
          </ChipInner>
        </Link>
      );
    }
    return (
      <a href={props.href} className={cls} aria-current={current}>
        <ChipInner icon={icon} count={count}>
          {children}
        </ChipInner>
      </a>
    );
  }
  return (
    <button type="button" className={cls} aria-pressed={selected} disabled={props.disabled} onClick={props.onClick}>
      <ChipInner icon={icon} count={count}>
        {children}
      </ChipInner>
    </button>
  );
}

export type FilterChipOption = {
  value: string;
  label: ReactNode;
  count?: number;
  icon?: ReactNode;
  /** 渡すと、この札は本物のリンクになる (頁を移る絞り込み)。渡さなければ onChange で絞る */
  href?: string;
};

type FilterChipsCommon = {
  /** 札の並びの名前 (「カテゴリー」「探索」など)。読み上げの名前にもなる */
  label: string;
  /** inline は名前を札の左に、above は札の上の小見出しに、hidden は読み上げだけ */
  labelPosition?: "inline" | "above" | "hidden";
  /** 選ばれている札の value。無ければどれも選ばない */
  value?: string | null;
  disabled?: boolean;
  LinkComponent?: LinkComponentType;
  className?: string;
};

/**
 * 全部の札に href があるときだけ onChange が要らない (全部リンクで、押す先が行き先にある)。
 * どれか 1 つでも href が無ければ、押したときの行き先が無いので onChange が必須
 */
export type FilterChipsProps = FilterChipsCommon &
  (
    | { options: readonly (FilterChipOption & { href: string })[]; onChange?: (value: string) => void }
    | { options: readonly FilterChipOption[]; onChange: (value: string) => void }
  );

/**
 * 札の並び。札が 5 つ以上で全部に印があるときは、狭い画面で 2 列に敷く (3、2、1 と折れて 1 つだけ残らないように)。
 * 「すべて」もほかの札と同じ 1 枡に置く (/luagate/textbook と同じ敷き方。奇数のときは最後の行が 1 つになる)
 */
export function FilterChips({ label, labelPosition = "inline", options, value, onChange, disabled = false, LinkComponent, className }: FilterChipsProps) {
  const choices: readonly FilterChipOption[] = options;
  const grid = choices.length >= 5 && choices.every((o) => o.icon);
  const above = labelPosition === "above";
  const chipsRef = useRef<HTMLDivElement>(null);
  // 札の字が替われば (同じ枚数でも) 測り直す
  const labelsKey = choices.map((o) => `${o.value}\u0000${typeof o.label === "string" ? o.label : ""}\u0000${o.count ?? ""}`).join("\u0001");
  // 2 列の格子で、半分の幅に字が 1 行で入らない札 (390 の「データとセキュリティ」) は 2 枡ぶんに広げる。幅が替わるたびに測り直す
  useLayoutEffect(() => {
    const el = chipsRef.current;
    if (!grid || !el || typeof ResizeObserver === "undefined") return;
    markWideChips(el);
    const ro = new ResizeObserver(() => markWideChips(el));
    ro.observe(el);
    // 字の形 (web フォント) が届くと字の幅が替わるが、並びの幅は替わらないので ResizeObserver は鳴らない。届いたときにも測り直す
    // (本番の 390 で、代わりの字で測って折れた「ネットワークと Web」が、字が届いたあとも広がったまま残った)
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    const remeasure = () => markWideChips(el);
    let alive = true;
    fonts?.ready.then(() => {
      if (alive) remeasure();
    });
    fonts?.addEventListener?.("loadingdone", remeasure);
    return () => {
      alive = false;
      ro.disconnect();
      fonts?.removeEventListener?.("loadingdone", remeasure);
    };
  }, [grid, labelsKey]);
  return (
    <div
      className={`lg-filter__group${above ? " lg-filter__group--above" : ""}${className ? ` ${className}` : ""}`}
      role="group"
      aria-label={label}
    >
      {labelPosition === "hidden" ? null : (
        <span className="lg-filter__group-label" aria-hidden="true">
          {label}
        </span>
      )}
      <div ref={chipsRef} className={`lg-filter__chips${grid ? " lg-filter__chips--grid" : ""}`}>
        {choices.map((o) => {
          const selected = value !== undefined && value !== null && o.value === value;
          return o.href !== undefined ? (
            <Chip key={o.value} href={o.href} LinkComponent={LinkComponent} selected={selected} icon={o.icon} count={o.count}>
              {o.label}
            </Chip>
          ) : (
            <Chip key={o.value} selected={selected} icon={o.icon} count={o.count} disabled={disabled} onClick={() => onChange?.(o.value)}>
              {o.label}
            </Chip>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 2 列の格子の札のうち、字が 2 行に折れた札に data-chip-wide を付ける (page-parts.css が 2 枡ぶんに広げる)。
 * 広げた札のすぐ前で段に 1 つだけ残る札も広げ、段の右に穴を空けない。
 * いったん全部を外してから測るので、画面が広がって 1 行に入るようになった札は元の 1 枡に戻る
 */
export function markWideChips(container: HTMLElement): void {
  const chips = [...container.querySelectorAll<HTMLElement>(".lg-chip")];
  for (const chip of chips) chip.removeAttribute("data-chip-wide");
  const wide = chips.filter((chip) => {
    const label = chip.querySelector<HTMLElement>(".lg-chip__label");
    if (!label) return false;
    const line = parseFloat(getComputedStyle(label).lineHeight) || parseFloat(getComputedStyle(label).fontSize) * 1.5;
    return label.offsetHeight > line * 1.5;
  });
  // 広げた札の前で 1 枡だけ残った札も広げる (段の右に穴を空けない)。札の順は替えない
  const set = new Set(wide);
  let waiting: HTMLElement | null = null;
  for (const chip of chips) {
    if (set.has(chip)) {
      if (waiting) set.add(waiting);
      waiting = null;
    } else waiting = waiting ? null : chip;
  }
  for (const chip of set) chip.setAttribute("data-chip-wide", "");
}
